import CreateEditOrganizationModal from '@components/organisms/organization/CreateEditOrganizationModal';
import AssignPlanModal from '@components/organisms/organization/AssignPlanModal';
import ExtendTrialModal from '@components/organisms/organization/ExtendTrialModal';
import { useDebounce } from '@hooks/useDebounce';
import {
  useOrganizations,
  useCreateOrganization,
  useUpdateOrganization,
  useToggleOrganizationStatus,
  useToggleInvoicingPaused,
  useBulkToggleInvoicingPaused,
  useBulkToggleOrganizationStatus,
} from '@hooks/api/organizations';
import {
  useAssignPlanToOrganization,
  useExtendOrganizationTrial,
} from '@hooks/api/subscription';
import { useState } from 'react';
import OrganizationsList from './list';
import { Button } from '@components/atoms/Button';
import { Organization, CompletenessGateMode } from '@models/organization.model';
import { useTranslation } from '@hooks/useTranslation';

interface OrganizationsPageProps {}

const OrganizationsPage: React.FC<OrganizationsPageProps> = () => {
  // search
  const t = useTranslation('organizations');
  const [search, setSearch] = useState<string>('');
  const debouncedSearchValue = useDebounce(search, 500);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };
  // filter
  const [filter, setFilter] = useState<string>('ENABLED');
  const handleFilterChange = (filter: string) => {
    setFilter(filter);
  };
  // sort
  const [orderBy, setOrderBy] = useState<string | null>('name');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // pagination
  const [page, setPage] = useState<number>(0);
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setRowsPerPage(event.target.value as unknown as number);
    setPage(0);
  };

  // API hooks
  const organizationsQuery = useOrganizations({
    page,
    limit: rowsPerPage,
    search: debouncedSearchValue,
    filter,
    orderBy: orderBy || 'name',
    orderDirection,
  });

  const createOrganizationMutation = useCreateOrganization();
  const updateOrganizationMutation = useUpdateOrganization();
  const toggleOrganizationStatusMutation = useToggleOrganizationStatus();
  const toggleInvoicingPausedMutation = useToggleInvoicingPaused();
  const bulkToggleInvoicingPausedMutation = useBulkToggleInvoicingPaused();
  const bulkToggleStatusMutation = useBulkToggleOrganizationStatus();
  const assignPlanMutation = useAssignPlanToOrganization();
  const extendTrialMutation = useExtendOrganizationTrial();

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showAssignPlanModal, setShowAssignPlanModal] = useState<boolean>(false);
  const [organizationToAssign, setOrganizationToAssign] =
    useState<Organization | null>(null);

  const [organizationToExtend, setOrganizationToExtend] =
    useState<Organization | null>(null);

  const handleAddOrganization = () => {
    setShowCreateModal(true);
  };

  const modalClose = (): void => {
    setShowCreateModal(false);
  };

  const toggleEnableDisableOrganization = (
    organizationId: string,
    enable: boolean = false,
  ) => {
    if (!organizationId) return;
    toggleOrganizationStatusMutation.mutate({ organizationId, enable });
  };

  const toggleInvoicingPaused = (organizationId: string, paused: boolean) => {
    if (!organizationId) return;
    toggleInvoicingPausedMutation.mutate({ organizationId, paused });
  };

  const bulkToggleInvoicingPaused = (ids: string[], paused: boolean) => {
    if (!ids.length) return;
    bulkToggleInvoicingPausedMutation.mutate({ ids, paused });
  };

  const bulkToggleStatus = (ids: string[], enable: boolean) => {
    if (!ids.length) return;
    bulkToggleStatusMutation.mutate({ ids, enable });
  };

  const createOrganization = (
    name: string,
    ownerEmail: string,
    ownerFirstName: string,
    ownerLastName: string,
    showAsSubcontractor: boolean,
    atavEnabled: boolean,
  ) => {
    if (!name) return;

    createOrganizationMutation.mutate(
      {
        name,
        ownerEmail,
        ownerFirstName,
        ownerLastName,
        showAsSubcontractor,
        atavEnabled,
      },
      {
        onSuccess: () => {
          setShowCreateModal(false);
        },
      },
    );
  };

  const editOrganization = (
    name: string,
    organizationId: string,
    showAsSubcontractor: boolean,
    atavEnabled: boolean,
    completenessGateMode: CompletenessGateMode,
    onSuccess?: () => void,
  ) => {
    if (!name || !organizationId) return;

    updateOrganizationMutation.mutate(
      {
        organizationId,
        name,
        showAsSubcontractor,
        atavEnabled,
        completenessGateMode,
      },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          onSuccess?.();
        },
      },
    );
  };

  const handleOpenAssignPlan = (org: Organization) => {
    setOrganizationToAssign(org);
    setShowAssignPlanModal(true);
  };

  const handleCloseAssignPlan = () => {
    setShowAssignPlanModal(false);
    setOrganizationToAssign(null);
  };

  const handleOpenExtendTrial = (org: Organization) => {
    setOrganizationToExtend(org);
  };

  const handleCloseExtendTrial = () => {
    setOrganizationToExtend(null);
  };

  const handleExtendTrial = (trialEndsAtIso: string) => {
    if (!organizationToExtend) return;
    extendTrialMutation.mutate(
      {
        organizationId: organizationToExtend.organizationId,
        trialEndsAt: trialEndsAtIso,
      },
      {
        onSuccess: () => {
          handleCloseExtendTrial();
        },
      },
    );
  };

  // The Assign plan note links here for a lapsed trial: swap one modal for the other.
  const handleExtendFromAssignPlan = (org: Organization) => {
    handleCloseAssignPlan();
    handleOpenExtendTrial(org);
  };

  const handleAssignPlan = (planId: string, billingPeriod: string) => {
    if (!organizationToAssign) return;
    assignPlanMutation.mutate(
      {
        organizationId: organizationToAssign.organizationId,
        planId,
        billingPeriod,
      },
      {
        onSuccess: () => {
          handleCloseAssignPlan();
        },
      },
    );
  };

  const updateShowAsSubcontractor = (
    name: string,
    showAsSubcontractor: boolean,
    organizationId?: string,
  ) => {
    if (!name || !organizationId) return;

    updateOrganizationMutation.mutate({
      organizationId,
      name,
      showAsSubcontractor,
    });
  };

  const updateAtavEnabled = (
    name: string,
    atavEnabled: boolean,
    organizationId?: string,
  ) => {
    if (!name || !organizationId) return;

    updateOrganizationMutation.mutate({
      organizationId,
      name,
      atavEnabled,
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <svg
            className="w-7 h-7 text-orange-400 dark:text-orange-300"
            fill="currentColor"
            viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
              clipRule="evenodd"
            />
          </svg>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t.title}
          </h1>
        </div>
        <Button variant="primary" onClick={handleAddOrganization}>
          {t.addOrganization}
        </Button>
      </div>

      <OrganizationsList
        page={page}
        search={search}
        filter={filter}
        orderBy={orderBy}
        rowsPerPage={rowsPerPage}
        orderDirection={orderDirection}
        handleChangePage={handleChangePage}
        handleRequestSort={handleRequestSort}
        handleSearchChange={handleSearchChange}
        handleFilterChange={handleFilterChange}
        handleEditOrganization={editOrganization}
        count={organizationsQuery.data?.count || 0}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        updateShowAsSubcontractor={updateShowAsSubcontractor}
        updateAtavEnabled={updateAtavEnabled}
        handleDisableOrganization={toggleEnableDisableOrganization}
        organizations={organizationsQuery.data?.organizations || []}
        handleEnableOrganization={(id) =>
          toggleEnableDisableOrganization(id, true)
        }
        handleToggleInvoicingPaused={toggleInvoicingPaused}
        handleBulkToggleInvoicingPaused={bulkToggleInvoicingPaused}
        handleBulkToggleStatus={bulkToggleStatus}
        handleAssignPlan={handleOpenAssignPlan}
        handleExtendTrial={handleOpenExtendTrial}
      />
      <CreateEditOrganizationModal
        isOpen={!!showCreateModal}
        onClose={modalClose}
        createOrganization={createOrganization}
        saving={
          createOrganizationMutation.isPending ||
          updateOrganizationMutation.isPending
        }
      />
      <AssignPlanModal
        isOpen={showAssignPlanModal}
        onClose={handleCloseAssignPlan}
        organization={organizationToAssign}
        onAssign={handleAssignPlan}
        isLoading={assignPlanMutation.isPending}
        onExtendFreePeriod={handleExtendFromAssignPlan}
      />
      <ExtendTrialModal
        isOpen={!!organizationToExtend}
        onClose={handleCloseExtendTrial}
        organization={organizationToExtend}
        onConfirm={handleExtendTrial}
        isLoading={extendTrialMutation.isPending}
      />
    </div>
  );
};

export default OrganizationsPage;
